import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import {
  Box,
  Flex,
  Button,
  Paragraph,
  useNotification,
  LoadingContainer,
  usePaginationReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import { useAuth } from '@sopranodesign/pulse-auth';

import {
  fetchConfluenceSpaces,
  getConfluenceSettings,
  getConfluenceOAuthRedirectUrl
} from '../api';
import ConfluenceSpacesList from './ConfluenceSpacesList';
import ConfluencePagesList from './ConfluencePagesList';
import ConfluenceModalManager from './ConfluenceModalManager';

const Confluence = () => {
  const { hasPermission } = useAuth();
  const canEditResource = hasPermission('edit generative ai resource');
  const location = useLocation();
  const history = useHistory();
  const { showSuccess, showError } = useNotification();

  const pagination = usePaginationReducer({
    pathname: '/confluence',
    fetchQueryKey: ['generative-ai/confluence']
  });

  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [isViewingPages, setIsViewingPages] = useState(false);
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [isAddSpacesLoading, setIsAddSpacesLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [spaceLoadingIds, setSpaceLoadingIds] = useState({});
  const [pageLoadingIds, setPageLoadingIds] = useState({});
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [errorIds, setErrorIds] = useState({});
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['generative-ai/confluence/', pagination.params],
    queryFn: () => getConfluenceSettings(pagination.params),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const responseData = useMemo(() => data?.data ?? {}, [data]);
  const settings = useMemo(() => responseData.settings ?? null, [responseData]);
  const paginatedSpaces = useMemo(() => responseData.spaces ?? [], [responseData]);
  const meta = useMemo(() => responseData.meta ?? [], [responseData]);
  const hasPending = useMemo(() => responseData.has_pending ?? false, [responseData]);

  useEffect(() => {
    const next = !isViewingPages && (settings?.status === 'pending_disconnect' || hasPending);
    setShouldRefetch(next);
  }, [hasPending, settings?.status, isViewingPages, paginatedSpaces]);

  // The OAuth popup navigates itself to this same URL after the Atlassian
  // callback redirects it here — the opener tab is untouched, so this only
  // fires in whichever tab actually completes the flow.
  useEffect(() => {
    const oauthStatus = new URLSearchParams(location.search).get('oauth');

    if (!oauthStatus) { return; }

    if (oauthStatus === 'connected') {
      showSuccess('Connected to Confluence via OAuth2');
      queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/'] });
    } else if (oauthStatus === 'denied') {
      showError('OAuth2 connection was not completed');
    } else if (oauthStatus === 'no_accessible_site') {
      showError('No accessible Confluence site was found for that Atlassian account');
    }

    history.replace({ search: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = (type, props = {}) => {
    setActiveModal(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalProps({});
  };

  useEffect(() => {
    if (!isViewingPages) {
      setPageLoadingIds({});
    }
  }, [isViewingPages]);

  const handleConnect = async () => {
    setIsConnecting(true);

    // Open a blank tab immediately while still inside the click handler so
    // popup blockers treat it as a direct user gesture — the URL is only
    // known once the API call below resolves.
    const popup = window.open('', '_blank');

    try {
      const { data: redirectData } = await getConfluenceOAuthRedirectUrl();
      popup.location.href = redirectData.authorize_url;
    } catch {
      popup.close();
      showError('Unable to start the OAuth2 connection with Confluence.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleAddSpaces = async () => {
    setIsAddSpacesLoading(true);
    try {
      const response = await fetchConfluenceSpaces();
      setAvailableSpaces(response.data ?? []);
      openModal('spaces');
    } finally {
      setIsAddSpacesLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading Confluence...</Paragraph>
      </Flex>
    );
  }

  return (
    <Flex col>
      {/* Initial connection UI */}
      {!settings && !isLoading && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Connect your Confluence account to sync knowledge base pages.</Paragraph>
            {canEditResource && (
              <Button
                icon="plus"
                variant="primary"
                loading={isConnecting}
                onClick={handleConnect}
              >
                Connect Confluence
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Space selection UI */}
      {settings && settings.status === 'connected' && paginatedSpaces.length === 0 && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Choose Confluence Spaces to Sync.</Paragraph>
            <Button
              icon="plus"
              variant="secondary"
              loading={isAddSpacesLoading}
              onClick={handleAddSpaces}
            >
              Select Spaces
            </Button>
          </Flex>
        </Box>
      )}

      {/* Reauthorization required UI */}
      {settings && settings.status === 'reauth_required' && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Confluence needs to be reconnected — the connection has expired.</Paragraph>
            {canEditResource && (
              <Button
                icon="refresh"
                variant="primary"
                loading={isConnecting}
                onClick={handleConnect}
              >
                Reconnect Confluence
              </Button>
            )}
          </Flex>
        </Box>
      )}

      {/* Disconnecting UI */}
      {settings && settings.status === 'pending_disconnect' && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Disconnecting your Confluence account. This may take a moment...</Paragraph>
          </Flex>
        </Box>
      )}

      {/* Spaces List */}
      {settings && settings.status === 'connected' && paginatedSpaces.length > 0 && (
        <ConfluenceSpacesList
          spaces={paginatedSpaces}
          settings={settings}
          loadingIds={spaceLoadingIds}
          isSyncing={isSyncingAll}
          onDisconnect={() => openModal('confirmation', { type: 'disconnect' })}
          onView={(id) => {
            setSelectedSpaceId(id);
            setIsViewingPages(true);
          }}
          onResync={(space) => openModal('confirmation', { type: 'resync', space })}
          onDelete={(space) => openModal('confirmation', { type: 'delete', space })}
          onAddSpaces={handleAddSpaces}
          onResyncAll={() => openModal('confirmation', { type: 'resyncAll' })}
          isAddSpacesLoading={isAddSpacesLoading}
          pagination={{ meta, props: pagination.props }}
        />
      )}

      {/* Pages List */}
      {selectedSpaceId && (
        <ConfluencePagesList
          spaceId={selectedSpaceId}
          handleHide={() => {
            setSelectedSpaceId(null);
            setIsViewingPages(false);
            queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/'] });
          }}
          onOpenRecrawlModal={(page) =>
            openModal('confirmation', { type: 'recrawl', page, spaceId: selectedSpaceId })
          }
          loadingIds={pageLoadingIds}
          errorIds={errorIds}
        />
      )}

      {/* Modal Manager */}
      <ConfluenceModalManager
        activeModal={activeModal}
        modalProps={modalProps}
        closeModal={closeModal}
        spaces={availableSpaces}
        setErrorIds={setErrorIds}
        setSpaceLoadingIds={setSpaceLoadingIds}
        setPageLoadingIds={setPageLoadingIds}
        setIsSyncingAll={setIsSyncingAll}
      />
    </Flex>
  );
};

export default Confluence;
